import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig } from '@/mocks/siteConfig';
import { getFormUrl } from '@/utils/formUtils';

const breadcrumbItems = [
  { label: '首页', to: '/' },
  { label: '关于我们' },
];

const timelineItems = [
  { year: '2024', title: '平台创立', desc: '51暗网安全指南正式上线，致力于中文网络安全教育' },
  { year: '2025', title: '内容扩展', desc: '新增案例分析和法律法规解读两大专题板块' },
  { year: '2026', title: '影响力提升', desc: '累计发布超过50篇深度文章，月访问用户突破10万' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '专业可靠',
    desc: '所有内容均经过网络安全专家审核，确保信息的准确性和时效性',
  },
  {
    icon: 'ri-eye-line',
    title: '公开透明',
    desc: '坚持客观中立的立场，既揭示暗网风险，也呈现技术的双面性',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户至上',
    desc: '以通俗易懂的方式呈现专业知识，让每个网民都能学会保护自己',
  },
  {
    icon: 'ri-government-line',
    title: '依法合规',
    desc: '严格遵守中国法律法规，所有内容均以教育和警示为目的',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formUrl, setFormUrl] = useState('');

  useEffect(() => {
    getFormUrl('联系我们')
      .then((url) => setFormUrl(url))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    if (!formUrl) {
      setFormError('表单暂时不可用，请稍后再试');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '提交失败，请稍后再试';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
    telephone: siteConfig.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '海淀区',
      streetAddress: '中关村网络安全产业园',
      addressCountry: 'CN',
    },
    foundingDate: siteConfig.foundingDate,
  };

  return (
    <>
      <SEO
        title="关于我们"
        description={`了解${siteConfig.name}的品牌故事、核心价值观与联系方式。我们致力于提供专业的中文网络安全教育内容。`}
        keywords="关于我们,品牌介绍,联系方式,网络安全教育,51暗网"
        canonicalUrl={`${siteConfig.url}/about`}
        schema={orgSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="py-14 md:py-20 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-3">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">关于我们</h1>
            <p className="text-base text-foreground-600 max-w-2xl">
              了解我们的品牌故事、核心价值观和团队使命
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5">品牌故事</h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  <strong>{siteConfig.name}</strong>诞生于2024年，由一群关注网络安全的专业人士创立。我们观察到，随着暗网话题日益受到公众关注，中文互联网上缺乏系统、客观、易懂的暗网安全教育内容。
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  我们的使命是<strong>用通俗易懂的方式普及暗网安全知识</strong>，帮助普通网民了解暗网的真实面貌，掌握个人信息防护技巧，远离网络违法风险。
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  我们相信，知识和意识是网络安全的第一道防线。在我们的平台上，你不会看到耸人听闻的标题或夸大其词的描述——只有经过专业审核的、客观准确的安全教育内容。
                </p>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20team%20working%20on%20cybersecurity%2C%20warm%20amber%20toned%20modern%20office%20environment%2C%20clean%20minimal%20workspace%2C%20digital%20security%20concept%20art%2C%20professional%20atmosphere%2C%20no%20people%20visible&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="品牌形象"
                  className="w-full h-full object-cover rounded-lg"
                  title={`${siteConfig.name} - 品牌形象`}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-14 md:py-20 bg-background-100/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">发展历程</h2>
            <div className="relative">
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-amber-200 md:-translate-x-px"></div>
              <div className="space-y-10">
                {timelineItems.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="absolute left-6 md:left-1/2 w-3 h-3 rounded-full bg-amber-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50"></div>
                    <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-2">
                        {item.year}
                      </span>
                      <h3 className="text-lg font-semibold text-foreground-950 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">核心价值观</h2>
            <p className="text-base text-foreground-600 text-center mb-12">我们坚守的信念，指引着每一项内容和每一次互动</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v) => (
                <div key={v.title} className="p-6 rounded-lg border border-background-200/70 bg-background-50 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 bg-background-100/60" id="contact">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">联系我们</h2>
            <p className="text-base text-foreground-600 text-center mb-10">
              有任何问题、建议或合作意向？欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div className="p-5 rounded-lg border border-background-200/70 bg-background-50 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-600">{siteConfig.email}</p>
              </div>
              <div className="p-5 rounded-lg border border-background-200/70 bg-background-50 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h3>
                <p className="text-sm text-foreground-600">{siteConfig.phone}</p>
              </div>
              <div className="p-5 rounded-lg border border-background-200/70 bg-background-50 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">办公地址</h3>
                <p className="text-sm text-foreground-600">{siteConfig.address}</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-950 mb-5">发送消息</h3>
              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors duration-200"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors duration-200"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors duration-200"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    消息 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors duration-200 resize-none"
                    placeholder="请输入你的消息（500字以内）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                />
                <div className="flex items-center justify-between">
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-medium text-sm whitespace-nowrap cursor-pointer transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    <i className="ri-send-plane-line ml-2"></i>
                  </button>
                  {formStatus === 'success' && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <i className="ri-check-line"></i>
                      消息已发送成功！
                    </span>
                  )}
                  {formStatus === 'error' && (
                    <span className="text-sm text-red-600 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}